/**
 * X3: connection status. A small pill at the bottom of the screen while the device is offline
 * ("You're offline…"), and a brief "Back online" when the connection returns. Announced politely
 * to screen readers. Was written earlier but never mounted; now mounted once in App.
 */
import React, { useEffect, useState } from 'react';
import { CheckCircle, WifiOff, X } from 'lucide-react';

export const BACK_ONLINE_MS = 3000;

const OfflineIndicator: React.FC = () => {
  const [online, setOnline] = useState<boolean>(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  const [visible, setVisible] = useState<boolean>(() => typeof navigator !== 'undefined' && !navigator.onLine);

  useEffect(() => {
    let timer: number | undefined;
    const goOnline = () => {
      setOnline(true); setVisible(true);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setVisible(false), BACK_ONLINE_MS);
    };
    const goOffline = () => { window.clearTimeout(timer); setOnline(false); setVisible(true); };
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => { window.clearTimeout(timer); window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); };
  }, []);

  return (
    <div role="status" aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-4 z-[9999] flex justify-center px-4 max-md:bottom-20" data-testid="offline-indicator">
      {visible && (
        <div className={`pointer-events-auto flex max-w-xl items-center gap-2.5 rounded-full px-4 py-2 text-sm font-medium shadow-lg ${online ? 'bg-emerald-600 text-white' : 'bg-amber-500 text-amber-950'}`}>
          {online ? (
            <><CheckCircle className="h-4 w-4 shrink-0" aria-hidden="true" />Back online</>
          ) : (
            <>
              <WifiOff className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>You&apos;re offline. You can still read what&apos;s on screen. Saving and sending need a connection; an order you&apos;re writing is kept on this device.</span>
              <button type="button" onClick={() => setVisible(false)} className="-mr-1 rounded-full p-1 hover:bg-black/10" aria-label="Hide offline message">
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default OfflineIndicator;
